import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type MouseHandlerDataParam,
} from 'recharts';
import { supabase } from '../lib/supabase';
import { fetchAllRows } from '../lib/fetchAll';
import { countActiveUsersSince } from '../lib/activeUsers';
import { CHART_COLORS } from '../lib/chartTheme';
import { timeAgo } from '../lib/timeAgo';
import { useNavBadges } from '../lib/useNavBadges';
import { useInboxUnreadCount } from '../lib/useInboxUnreadCount';
import {
  DAY_TZ,
  OverviewDrilldownModal,
  dayKeyInTz,
  fetchVenueSeed,
  type DaySeriesKind,
  type DrilldownTarget,
} from '../components/OverviewDrilldownModal';
import { CityVenueDetailModal, type VenueDetailSeed } from '../components/CityVenueDetailModal';
import {
  WeekDrilldownPicker,
  type WeekDrilldownTarget,
  type WeekPickerDay,
} from '../components/WeekDrilldownPicker';
import { PageHeader } from '../ui/PageHeader';
import { StatCard } from '../ui/StatCard';
import { SkeletonCards } from '../ui/Skeleton';
import { EmptyState } from '../ui/EmptyState';
import { ErrorBanner } from '../ui/ErrorBanner';

const CHART_DAYS = 30;
/**
 * "All" is a real day count, not a special case: days since the first production signup era,
 * recomputed at module load so the window keeps growing. Everything downstream (template, dChart,
 * weekly grouping, drill-downs) treats it like any other N.
 */
const APP_EPOCH_ISO = '2026-04-01';
/**
 * Calendar days from `dayIso` through TODAY (Central), inclusive of the anchor and of nothing
 * before it — computed on Central day keys so the template's first day IS the anchor day exactly.
 */
function daysSince(dayIso: string): number {
  const todayCentral = dayKeyInTz(new Date());
  return Math.max(1, Math.round((Date.parse(todayCentral) - Date.parse(dayIso)) / 86400000) + 1);
}
const ALL_DAYS = Math.max(120, daysSince(APP_EPOCH_ISO));
/** Milestone anchors (Evan 2026-09-08) — recomputed at load, so the windows keep growing. */
const SINCE_JUN19 = daysSince('2026-06-19');
const SINCE_JUL3 = daysSince('2026-07-03');
const RANGE_OPTIONS: { v: number; label: string }[] = (() => {
  const base: { v: number; label: string }[] = [
    { v: 7, label: '7d' },
    { v: 14, label: '14d' },
    { v: 30, label: '30d' },
    { v: 60, label: '60d' },
    { v: 90, label: '90d' },
    { v: 120, label: '120d' },
    { v: SINCE_JUL3, label: 'Since Jul 3' },
    { v: SINCE_JUN19, label: 'Since Jun 19' },
    { v: ALL_DAYS, label: 'All' },
  ];
  // A milestone will eventually land on a fixed value (Jun 19 = 120d in mid-October); the labeled
  // pill wins so there is never a duplicate React key or a double-active pair.
  const seen = new Map<number, { v: number; label: string }>();
  for (const o of base) seen.set(o.v, o);
  return Array.from(seen.values()).sort((a, b) => a.v - b.v);
})();
const CHART_DAYS_OPTIONS = RANGE_OPTIONS.map((o) => o.v);
const CHART_DAYS_STORAGE_KEY = 'admin:home:chartDays';
/** Legacy single-axis preference ('daily' | 'trend'). Read once to seed the presentation axis. */
const LEGACY_CHART_VIEW_STORAGE_KEY = 'admin:home:chartView';
const CHART_GRANULARITY_STORAGE_KEY = 'admin:home:chartGranularity';
const CHART_PRESENTATION_STORAGE_KEY = 'admin:home:chartPresentation';

/**
 * The chart controls are two INDEPENDENT axes (Evan, 2026-08-02): how wide a bucket is, and how it
 * is drawn. The old single `chartView` pill conflated them — 'daily' meant "bars of the per-day
 * increment" and 'trend' meant "area of the cumulative total", so there was no way to ask for
 * weekly bars or for both at once.
 */
type Granularity = 'daily' | 'weekly';
type Presentation = 'bar' | 'trend' | 'combined';

const GRANULARITY_OPTIONS: { value: Granularity; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
];

const PRESENTATION_OPTIONS: { value: Presentation; label: string }[] = [
  { value: 'bar', label: 'Bar' },
  { value: 'trend', label: 'Trend' },
  { value: 'combined', label: 'Combined' },
];

/**
 * Evan (2026-08-02, correcting an earlier misread): the Overview's OWN sections must never
 * collapse — "I don't want the things within the overview page to be collapsed." Collapsing lives
 * on the sidebar nav instead (`src/App.tsx` `NavSections`). Every section below renders expanded,
 * always; there is deliberately no open/closed state and no `admin:home:openSections` key.
 */

function readStoredGranularity(): Granularity {
  try {
    return window.localStorage.getItem(CHART_GRANULARITY_STORAGE_KEY) === 'weekly' ? 'weekly' : 'daily';
  } catch {
    return 'daily';
  }
}

/** Falls back to the legacy `chartView` key so an operator's existing Trend preference survives. */
function readStoredPresentation(): Presentation {
  try {
    const raw = window.localStorage.getItem(CHART_PRESENTATION_STORAGE_KEY);
    if (raw === 'bar' || raw === 'trend' || raw === 'combined') return raw;
    return window.localStorage.getItem(LEGACY_CHART_VIEW_STORAGE_KEY) === 'trend' ? 'trend' : 'bar';
  } catch {
    return 'bar';
  }
}

/** Daemon heartbeat older than this reads as stale. */
const DAEMON_STALE_MS = 10 * 60 * 1000;

function readStoredChartDays(): number {
  try {
    const raw = window.localStorage.getItem(CHART_DAYS_STORAGE_KEY);
    const n = raw ? Number(raw) : NaN;
    // A stored "All" was yesterday's day-count; anything past 120 re-resolves to today's All.
    if (n > 120) return ALL_DAYS;
    return CHART_DAYS_OPTIONS.includes(n) ? n : CHART_DAYS;
  } catch {
    return CHART_DAYS;
  }
}

type HomeStats = {
  totalUsers: number;
  newUsers7d: number;
  venuesRatedDistinct: number;
  /** Users who have EVER rated (distinct, non-retired logs). */
  distinctRaters: number;
  totalRatings: number;
  ratings7d: number;
  citiesWithRaters: number;
  /** null = source unavailable (shown as "—") rather than a real zero. */
  active7d: number | null;
  active30d: number | null;
};

/** "Last 24 hours" activity strip — DB-truth counts, same fail-soft-to-null pattern as HomeStats. */
type Last24hStats = {
  newUsers: number | null;
  ratings: number | null;
  photosSubmitted: number | null;
  photosApproved: number | null;
};

type DaySeriesPoint = { iso: string; label: string; value: number };

type TopCity = { id: string; name: string | null; total_shado_ratings: number | null };
type TopVenue = { id: string; venue_name_user_input: string | null; venue_shado_rating_count: number | null };
type RecentSignup = { id: string; username: string | null; created_at: string };
type RecentRating = { id: string; venue_id: string | null; venue_name: string | null; created_at: string };
type TopContributor = { user_id: string; username: string | null; count: number };

/**
 * System-health snapshot. Every field is fail-soft: `null` = source unreadable
 * from the client (RLS/grant) → that segment renders as "no signal", never an error.
 */
type HealthState = {
  daemonBeatAt: string | null;
  daemonReadable: boolean;
  sentry24h: number | null;
  sentryTopTitle: string | null;
  cronAlarm24h: number | null; // null = table not readable → segment hidden
};

/**
 * All day buckets cut at midnight US Central — Evan's clock, not UTC.
 *
 * `DAY_TZ` / `dayKeyInTz` live in `OverviewDrilldownModal` and are imported here on purpose: the
 * chart bucket key and the drill-down's SQL range MUST be produced by the same code, or clicking
 * "Jul 31 · 3 new users" could list a different set of rows than the bar counted.
 */
const dayKey = dayKeyInTz;

function buildLastNDaysTemplate(n: number): DaySeriesPoint[] {
  const [y, m, d] = dayKeyInTz(new Date()).split('-').map(Number);
  const out: DaySeriesPoint[] = [];
  for (let i = n - 1; i >= 0; i--) {
    // Noon UTC of the Central calendar day — DST-proof day stepping.
    const dt = new Date(Date.UTC(y, m - 1, d - i, 12));
    out.push({
      iso: dt.toISOString().slice(0, 10),
      label: dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' }),
      value: 0,
    });
  }
  return out;
}

function mergeCountsIntoSeries(series: DaySeriesPoint[], counts: Map<string, number>): DaySeriesPoint[] {
  return series.map((p) => ({ ...p, value: counts.get(p.iso) ?? 0 }));
}

function countByDay(rows: { created_at: string }[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const r of rows) {
    const day = dayKey(r.created_at);
    m.set(day, (m.get(day) ?? 0) + 1);
  }
  return m;
}

/** Central day each venue got its FIRST (non-retired) rating — the honest "distinct venues" increment. */
function firstRatingDayByVenue(rows: { created_at: string; venue_id: string | null }[]): Map<string, string> {
  const firstDay = new Map<string, string>();
  for (const r of rows) {
    if (!r.venue_id) continue;
    const day = dayKey(r.created_at);
    const prev = firstDay.get(r.venue_id);
    if (!prev || day < prev) firstDay.set(r.venue_id, day);
  }
  return firstDay;
}

function sumOf(series: DaySeriesPoint[]): number {
  return series.reduce((a, p) => a + p.value, 0);
}

// ---------------------------------------------------------------------------
// Chart buckets — one derivation path for both granularities.
// ---------------------------------------------------------------------------

/**
 * One plotted bucket: a Central day, or a week made of Central days.
 *
 * `value` is the bucket's own increment (what a bar draws) and `cumulative` is the running total at
 * the END of the bucket (what a line draws). Carrying both means Combined needs no second series,
 * and switching presentation never re-derives the data — only which key is drawn.
 */
type ChartPoint = {
  /** Daily: the Central day. Weekly: the Monday that starts the week. */
  key: string;
  label: string;
  value: number;
  cumulative: number;
  /** The Central days this bucket covers — length 1 in Daily. Drives the drill-down. */
  days: DaySeriesPoint[];
  /** Weekly only: fewer than 7 days covered (window edge, or the in-progress week). */
  partial: boolean;
};

/**
 * Monday that starts the ISO-8601 week containing `dayIso`.
 *
 * WHY MONDAY: this is a nightlife product, so the week must not cut a night in half. A Saturday
 * night that runs past midnight lands on Sunday in Central time (`dayKeyInTz`), and with a
 * Monday-start week Sunday is the week's LAST day — Saturday night and its Sunday-morning spillover
 * stay in the same bucket. A Sunday-start week would split that one night across two bars. It is
 * also ISO-8601, which is what every other weekly report we compare against uses.
 *
 * Derived from the day key string itself (pure UTC arithmetic on a date-only value), so weeks can
 * never drift from the days that `dayKeyInTz` produced — there is no second timezone path here.
 */
function weekStartIso(dayIso: string): string {
  const [y, m, d] = dayIso.split('-').map(Number);
  // Noon UTC of the Central calendar date — DST can never shift which weekday this is.
  const dow = new Date(Date.UTC(y, m - 1, d, 12)).getUTCDay(); // 0 = Sunday
  const back = (dow + 6) % 7; // 0 = Monday
  return new Date(Date.UTC(y, m - 1, d - back, 12)).toISOString().slice(0, 10);
}

function fmtDayShort(dayIso: string): string {
  const [y, m, d] = dayIso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/** "Jul 20–26" within a month, "Jul 27–Aug 2" across one. Labels the days actually covered. */
function weekRangeLabel(firstIso: string, lastIso: string): string {
  if (firstIso === lastIso) return fmtDayShort(firstIso);
  const sameMonth = firstIso.slice(0, 7) === lastIso.slice(0, 7);
  return `${fmtDayShort(firstIso)}–${sameMonth ? String(Number(lastIso.slice(8, 10))) : fmtDayShort(lastIso)}`;
}

/** Per-day points with the running total carried alongside each day's increment. */
function buildDailyPoints(series: DaySeriesPoint[], baseline: number): ChartPoint[] {
  let acc = baseline;
  return series.map((p) => {
    acc += p.value;
    return { key: p.iso, label: p.label, value: p.value, cumulative: acc, days: [p], partial: false };
  });
}

/**
 * Weekly buckets are a GROUPING of the daily points — never a second pass over dates or rows.
 * A week's increment is the sum of its days and its cumulative is its last day's cumulative, so the
 * weekly line lands exactly on the daily line at every week boundary, and no extra query is made.
 */
function groupPointsByWeek(daily: ChartPoint[]): ChartPoint[] {
  const weeks: ChartPoint[] = [];
  for (const point of daily) {
    const key = weekStartIso(point.key);
    const current = weeks.length > 0 ? weeks[weeks.length - 1] : null;
    if (current && current.key === key) {
      current.value += point.value;
      current.cumulative = point.cumulative;
      current.days.push(point.days[0]);
    } else {
      weeks.push({ key, label: '', value: point.value, cumulative: point.cumulative, days: [point.days[0]], partial: false });
    }
  }
  for (const w of weeks) {
    w.label = weekRangeLabel(w.days[0].iso, w.days[w.days.length - 1].iso);
    w.partial = w.days.length < 7;
  }
  return weeks;
}

/**
 * "More" (Evan 2026-09-08): summary stats over the buckets currently plotted — so Range and
 * Daily/Weekly are respected — with a histogram of the bucket values one layer deeper (a popup
 * within the popup). Pure derivation from the same ChartPoints the chart draws; no extra query.
 */
function ChartMoreButton({
  title,
  bucketNoun,
  valueNoun,
  points,
}: {
  title: string;
  /** "days" or "weeks" — whatever the granularity currently plots. */
  bucketNoun: string;
  /** what one bar counts, e.g. "ratings", "distinct raters". */
  valueNoun: string;
  points: ChartPoint[];
}) {
  const [open, setOpen] = useState(false);
  const [hist, setHist] = useState(false);
  /** null = auto (~12 bins). The slider hands the operator the bin width directly. */
  const [binWidth, setBinWidth] = useState<number | null>(null);

  const stats = useMemo(() => {
    if (points.length === 0) return null;
    const values = points.map((p) => p.value);
    const sorted = [...values].sort((a, b) => a - b);
    const total = sorted.reduce((a, v) => a + v, 0);
    const mid = Math.floor(sorted.length / 2);
    const median = sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
    let hiIdx = 0;
    values.forEach((v, i) => {
      if (v > values[hiIdx]) hiIdx = i;
    });
    return {
      avg: total / sorted.length,
      median,
      high: values[hiIdx],
      highLabel: points[hiIdx]?.label ?? '',
      low: sorted[0],
      total,
      n: sorted.length,
    };
  }, [points]);

  /** Value-distribution bins. Auto picks ~12 even bins; the slider overrides the width. */
  const histo = useMemo(() => {
    if (points.length === 0) return { bins: [] as { label: string; count: number }[], width: 1, max: 0 };
    const values = points.map((p) => p.value);
    const max = Math.max(...values);
    const auto = Math.max(1, Math.ceil((max + 1) / 12));
    const width = Math.max(1, Math.min(binWidth ?? auto, Math.max(1, max)));
    /* Never more than 12 bars: past that, the LAST bin goes open-ended ("21+") and swallows every
       larger value — so the outlier days stay visible instead of stranding off the right edge
       behind a run of empty bins (Evan 2026-09-08). */
    const MAX_BINS = 12;
    const totalBins = Math.ceil((max + 1) / width);
    const nBins = Math.min(MAX_BINS, totalBins);
    const openEnded = totalBins > nBins;
    const out: { label: string; count: number }[] = [];
    for (let b = 0; b < nBins; b++) {
      const lo = b * width;
      const isLast = b === nBins - 1;
      out.push({
        label:
          openEnded && isLast ? `${lo}+` : width === 1 ? String(lo) : `${lo}–${lo + width - 1}`,
        count: 0,
      });
    }
    values.forEach((v) => {
      out[Math.min(nBins - 1, Math.floor(v / width))].count += 1;
    });
    return { bins: out, width, max };
  }, [points, binWidth]);

  if (!stats) return null;

  const row = (label: string, value: string, sub?: string) => (
    <div className="flex items-baseline justify-between gap-4 border-b border-white/5 py-2 last:border-0">
      <span className="text-[12px] text-mutedDim">{label}</span>
      <span className="text-right text-[13px] tabular-nums text-white">
        {value}
        {sub ? <span className="ml-2 text-[11px] text-mutedDim">{sub}</span> : null}
      </span>
    </div>
  );

  return (
    <>
      <div className="mt-2 flex justify-end">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-[11px] font-medium text-mutedDim transition-colors hover:text-white"
        >
          More →
        </button>
      </div>
      {open ? (
        <div
          className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/70 p-4 sm:p-10"
          role="dialog"
          onClick={() => {
            setHist(false);
            setOpen(false);
          }}
        >
          <div className="admin-card w-full max-w-md !p-5" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <div className="text-[13px] font-semibold text-white">{title}</div>
                <div className="text-[11px] text-mutedDim">
                  {stats.n} {bucketNoun} plotted · {valueNoun} per {bucketNoun.replace(/s$/, '')}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="admin-btn admin-btn-secondary admin-btn-sm"
              >
                Close
              </button>
            </div>
            {row('Average', stats.avg.toFixed(1))}
            {row('Median', String(stats.median))}
            {row('High', String(stats.high), stats.highLabel)}
            {row('Low', String(stats.low))}
            {row('Total across range', stats.total.toLocaleString())}
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                onClick={() => setHist(true)}
                className="admin-btn admin-btn-secondary admin-btn-sm"
              >
                Histogram
              </button>
            </div>
          </div>
          {hist ? (
            <div
              className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-black/70 p-4 sm:p-14"
              role="dialog"
              onClick={(e) => {
                e.stopPropagation();
                setHist(false);
              }}
            >
              <div className="admin-card w-full max-w-lg !p-5" onClick={(e) => e.stopPropagation()}>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[13px] font-semibold text-white">{title} — histogram</div>
                    <div className="text-[11px] text-mutedDim">
                      how many {bucketNoun} landed at each {valueNoun} count
                    </div>
                    <label className="mt-2 flex items-center gap-2 text-[11px] text-mutedDim">
                      Bin size
                      <input
                        type="range"
                        min={1}
                        max={Math.max(2, histo.max)}
                        value={histo.width}
                        onChange={(e) => setBinWidth(Number(e.target.value))}
                        className="w-40 accent-accent"
                      />
                      <span className="tabular-nums text-white">
                        {histo.width} {histo.width === 1 ? valueNoun.replace(/s$/, '') : valueNoun} per bar
                      </span>
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={() => setHist(false)}
                    className="admin-btn admin-btn-secondary admin-btn-sm"
                  >
                    Close
                  </button>
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={histo.bins} margin={{ top: 8, right: 6, left: 0, bottom: 2 }}>
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 10, fill: '#9A9AA2' }}
                      tickLine={false}
                      axisLine={{ stroke: 'rgba(255,255,255,0.12)' }}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 10, fill: '#9A9AA2' }}
                      tickLine={false}
                      axisLine={false}
                      width={26}
                    />
                    <Tooltip
                      cursor={{ fill: 'rgba(139,92,246,0.08)' }}
                      contentStyle={{
                        background: '#161718',
                        border: '1px solid #2A2B2F',
                        borderRadius: 10,
                        fontSize: 12,
                      }}
                      labelStyle={{ color: '#9A9AA2' }}
                      formatter={(value) => [String(value), bucketNoun]}
                      labelFormatter={(l) => `${l} ${valueNoun}`}
                    />
                    <Bar dataKey="count" fill="#8B5CF6" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

function chartCaption(
  presentation: Presentation,
  granularity: Granularity,
  chartDays: number,
  increment: string,
  total: string,
): string {
  const per = granularity === 'weekly' ? 'per week' : 'per day';
  const stepwise = granularity === 'weekly' ? 'week by week' : 'day by day';
  const range = `last ${chartDays} days`;
  if (presentation === 'bar') return `${increment} ${per} · ${range}`;
  if (presentation === 'trend') return `${total}, ${stepwise} · ${range}`;
  return `${total} (line) over ${increment.toLowerCase()} ${per} (bars) · ${range}`;
}

function isoHoursAgo(h: number): string {
  const d = new Date();
  d.setTime(d.getTime() - h * 3600000);
  return d.toISOString();
}

function isoDaysAgo(d: number): string {
  const x = new Date();
  x.setTime(x.getTime() - d * 86400000);
  return x.toISOString();
}

/** Client-side aggregation (no RPC yet): every venue_photos row that was ever approved
 * (approved_at set — survives later soft-delete), grouped by submitter, top N by count. */
async function fetchTopPhotoContributors(limit = 10): Promise<TopContributor[]> {
  try {
    const rows = await fetchAllRows<{ user_id: string | null }>((from, to) =>
      supabase.from('venue_photos').select('user_id').not('approved_at', 'is', null).range(from, to),
    );
    const counts = new Map<string, number>();
    for (const r of rows) {
      if (!r.user_id) continue;
      counts.set(r.user_id, (counts.get(r.user_id) ?? 0) + 1);
    }
    const top = Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, limit);
    if (top.length === 0) return [];
    const ids = top.map(([id]) => id);
    const { data, error } = await supabase.from('profiles').select('id, username').in('id', ids);
    if (error) throw error;
    const nameById = new Map((data ?? []).map((p) => [p.id as string, p.username as string | null]));
    return top.map(([id, count]) => ({ user_id: id, username: nameById.get(id) ?? null, count }));
  } catch {
    return [];
  }
}

/**
 * Combined ("price and volume") layout, expressed as fractions of the plot height:
 * bars live in the bottom band, the line lives above it, and the divider sits between them.
 * Both are drawn by ONE `ComposedChart` over ONE data array with ONE `XAxis`, so the two panes
 * cannot drift horizontally — every bucket has a single x band shared by the bar and the line
 * point, and the tooltip cursor is a single full-height rect spanning both.
 */
const VOLUME_PANE_FRACTION = 0.28;
const VOLUME_DIVIDER_FRACTION = 0.33;
const LINE_FLOOR_FRACTION = 0.42;
const LINE_CEIL_FRACTION = 0.97;

function StatSpark({
  data,
  caption,
  presentation,
  granularity,
  onPointClick,
}: {
  data: ChartPoint[];
  caption: string;
  presentation: Presentation;
  granularity: Granularity;
  /** Click a bucket → drill into the rows behind it. */
  onPointClick?: (point: ChartPoint) => void;
}) {
  const gradId = useId();
  const hasData = useMemo(() => data.some((d) => d.value > 0), [data]);
  /**
   * Two y-domains stacked inside one plot area. The volume axis is inflated so the tallest bar
   * reaches only `VOLUME_PANE_FRACTION` of the height; the line axis is padded below so the
   * cumulative series occupies the band from `LINE_FLOOR_FRACTION` to `LINE_CEIL_FRACTION`.
   * Both axes are hidden — they exist purely to place the two panes.
   */
  const combined = useMemo(() => {
    const volumeMax = Math.max(1, ...data.map((d) => d.value));
    const lineMin = data.length > 0 ? Math.min(...data.map((d) => d.cumulative)) : 0;
    const lineMax = data.length > 0 ? Math.max(...data.map((d) => d.cumulative)) : 1;
    // A perfectly flat line (no growth in the window) has zero spread — give it a nominal band so
    // the domain math can't divide by zero and the line renders mid-pane instead of vanishing.
    const spread = lineMax - lineMin || Math.max(1, lineMax * 0.02);
    const lineSpan = spread / (LINE_CEIL_FRACTION - LINE_FLOOR_FRACTION);
    const lineLo = lineMin - LINE_FLOOR_FRACTION * lineSpan;
    return {
      volumeDomain: [0, volumeMax / VOLUME_PANE_FRACTION] as [number, number],
      lineDomain: [lineLo, lineLo + lineSpan] as [number, number],
      dividerY: (volumeMax / VOLUME_PANE_FRACTION) * VOLUME_DIVIDER_FRACTION,
    };
  }, [data]);
  /**
   * Recharts 3 hands the chart-level handler a `MouseHandlerDataParam`, whose `activeTooltipIndex`
   * is `number | string | null | undefined` (`TooltipIndex = string | null`). Guard the null/empty
   * cases explicitly — `Number(null)` and `Number('')` are both `0`, which would silently drill
   * into the FIRST bar on any click that had no active bucket.
   */
  const handleChartClick = useCallback(
    (state: MouseHandlerDataParam) => {
      if (!onPointClick) return;
      const raw = state?.activeTooltipIndex;
      if (raw === null || raw === undefined) return;
      const idx = typeof raw === 'number' ? raw : parseInt(raw, 10);
      if (!Number.isInteger(idx) || idx < 0 || idx >= data.length) return;
      onPointClick(data[idx]);
    },
    [data, onPointClick],
  );
  const clickable = Boolean(onPointClick) && hasData;
  const tooltipProps = {
    contentStyle: {
      background: CHART_COLORS.surfaceElevated,
      border: `1px solid ${CHART_COLORS.borderStrong}`,
      borderRadius: '8px',
      fontSize: '12px',
      color: CHART_COLORS.textPrimary,
    },
    labelStyle: { color: CHART_COLORS.mutedDim },
    // Combined plots two series, so it is the only mode where the series name carries meaning.
    formatter: (value: unknown, name: unknown) =>
      [Number(value ?? 0).toLocaleString(), presentation === 'combined' ? String(name ?? '') : ''] as [
        string,
        string,
      ],
  };
  const xAxisProps = {
    dataKey: 'label',
    tick: { fill: CHART_COLORS.mutedDim, fontSize: 9 },
    tickLine: false,
    axisLine: { stroke: CHART_COLORS.borderStrong },
    interval: 'preserveStartEnd' as const,
  };
  const barLabel = granularity === 'weekly' ? 'This week' : 'This day';
  return (
    <div className="mt-4">
      <div
        className={`${presentation === 'combined' ? 'h-[148px]' : 'h-[104px]'} w-full min-w-0 ${
          clickable ? 'cursor-pointer' : ''
        }`}
      >
        {hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            {presentation === 'bar' ? (
              <BarChart
                data={data}
                margin={{ top: 6, right: 4, left: 0, bottom: 2 }}
                barCategoryGap="12%"
                onClick={handleChartClick}
              >
                <XAxis {...xAxisProps} />
                <YAxis hide domain={[0, 'dataMax']} />
                <Tooltip cursor={{ fill: 'rgba(255,255,255,0.06)' }} {...tooltipProps} />
                <Bar dataKey="value" fill={CHART_COLORS.accent} radius={[3, 3, 0, 0]} maxBarSize={28} />
              </BarChart>
            ) : presentation === 'trend' ? (
              <AreaChart data={data} margin={{ top: 6, right: 4, left: 0, bottom: 2 }} onClick={handleChartClick}>
                <defs>
                  <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART_COLORS.accent} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={CHART_COLORS.accent} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <XAxis {...xAxisProps} />
                <YAxis hide domain={['auto', 'dataMax']} />
                <Tooltip cursor={{ stroke: CHART_COLORS.borderStrong }} {...tooltipProps} />
                <Area
                  type="monotone"
                  dataKey="cumulative"
                  stroke={CHART_COLORS.accent}
                  strokeWidth={2}
                  fill={`url(#${gradId})`}
                  dot={false}
                  activeDot={{ r: 3 }}
                />
              </AreaChart>
            ) : (
              /* Combined — the stock-chart layout: cumulative line on top, volume bars beneath,
                 one chart, one x-axis. The cursor rect spans BOTH panes, which is also the proof
                 they are aligned: it is a single rectangle over a single x band. */
              <ComposedChart
                data={data}
                margin={{ top: 6, right: 4, left: 0, bottom: 2 }}
                barCategoryGap="18%"
                onClick={handleChartClick}
              >
                <defs>
                  <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART_COLORS.accent} stopOpacity={0.28} />
                    <stop offset="100%" stopColor={CHART_COLORS.accent} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis {...xAxisProps} />
                <YAxis yAxisId="line" hide domain={combined.lineDomain} />
                <YAxis yAxisId="volume" hide domain={combined.volumeDomain} />
                <Tooltip cursor={{ fill: 'rgba(255,255,255,0.06)' }} {...tooltipProps} />
                <ReferenceLine
                  yAxisId="volume"
                  y={combined.dividerY}
                  stroke={CHART_COLORS.borderStrong}
                  strokeDasharray="2 3"
                />
                <Bar
                  yAxisId="volume"
                  dataKey="value"
                  name={barLabel}
                  fill={CHART_COLORS.accent}
                  fillOpacity={0.45}
                  radius={[2, 2, 0, 0]}
                  maxBarSize={20}
                  isAnimationActive={false}
                />
                <Area
                  yAxisId="line"
                  type="monotone"
                  dataKey="cumulative"
                  stroke="none"
                  fill={`url(#${gradId})`}
                  dot={false}
                  activeDot={false}
                  legendType="none"
                  tooltipType="none"
                  isAnimationActive={false}
                />
                <Line
                  yAxisId="line"
                  type="monotone"
                  dataKey="cumulative"
                  name="Total"
                  stroke={CHART_COLORS.accent}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 3 }}
                  /**
                   * ANIMATION OFF IS THE BUG FIX, NOT A STYLE CHOICE — do not re-enable.
                   * Recharts animates a `Line` by measuring the SVG path with `getTotalLength()`
                   * and driving a `strokeDasharray` from 0 to that length. It reads the length
                   * during render off `pathRef.current`, which still holds the PREVIOUS path
                   * (`es6/cartesian/Line.js` :329). When the data or plot geometry changes — this
                   * card's series arrives in stages, and Combined re-mounts at a different height
                   * than Bar/Trend — the final frame lands on a dasharray computed from the older,
                   * SHORTER path, so the stroke stops partway across and the remainder falls in the
                   * dash gap. Nothing re-renders afterwards, so it stays truncated.
                   * The `Area` under it animates via a clip rect instead, so its fill kept painting
                   * to the right edge — that mismatch was the visible symptom.
                   * With `isAnimationActive={false}` recharts emits no dasharray at all.
                   * Bar and Area above are also frozen so the three series appear together.
                   */
                  isAnimationActive={false}
                />
              </ComposedChart>
            )}
          </ResponsiveContainer>
        ) : (
          <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-border/80 text-[11px] text-mutedDim">
            No activity in this window
          </div>
        )}
      </div>
      <p className="mt-2 text-[10px] text-mutedDim leading-snug">
        {caption}
        {clickable ? (
          <span className="text-accent">
            {granularity === 'weekly'
              ? ' · click a week to pick a day inside it'
              : ' · click a day for the rows behind it'}
          </span>
        ) : null}
      </p>
    </div>
  );
}

/** One segment of the health strip: status dot + label + detail. */
function HealthSegment({
  tone,
  label,
  detail,
}: {
  tone: 'ok' | 'warn' | 'none';
  label: string;
  detail: string;
}) {
  const dot =
    tone === 'ok' ? 'bg-success' : tone === 'warn' ? 'bg-warning animate-pulse' : 'bg-mutedDim';
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dot}`} />
      <span className="shrink-0 text-[12px] font-medium text-textPrimary">{label}</span>
      <span className="truncate text-[12px] text-mutedDim">{detail}</span>
    </div>
  );
}

/** Tap-through queue card: count + destination. */
function QueueCard({ to, label, count, verb }: { to: string; label: string; count: number; verb: string }) {
  return (
    <Link
      to={to}
      className="admin-card admin-card-hover group flex min-w-0 items-center justify-between gap-3 !p-4"
    >
      <div className="min-w-0">
        <div className="admin-eyebrow mb-1">{label}</div>
        <div
          className={`text-[22px] font-semibold leading-tight tabular-nums tracking-[-0.02em] ${
            count > 0 ? 'text-textPrimary' : 'text-mutedDim'
          }`}
        >
          {count.toLocaleString()}
        </div>
      </div>
      <span className="shrink-0 text-[12px] text-mutedDim transition-colors group-hover:text-accent">
        {verb} →
      </span>
    </Link>
  );
}

export function HomeScreen() {
  const [stats, setStats] = useState<HomeStats | null>(null);
  const [userSignupSeries, setUserSignupSeries] = useState<DaySeriesPoint[]>([]);
  const [ratingsSeries, setRatingsSeries] = useState<DaySeriesPoint[]>([]);
  const [venuesRatedDaySeries, setVenuesRatedDaySeries] = useState<DaySeriesPoint[]>([]);
  /** user_ids who rated on each Central day in the window — bar heights AND weekly unions. */
  const [ratersActiveByDay, setRatersActiveByDay] = useState<Record<string, string[]>>({});
  const [ratersFirstSeries, setRatersFirstSeries] = useState<DaySeriesPoint[]>([]);
  const [ratersTrendBaseline, setRatersTrendBaseline] = useState(0);
  /** user_ids whose first-ever rating landed on each day — the 'new-raters' drill-down list. */
  const [firstRaterIdsByDay, setFirstRaterIdsByDay] = useState<Record<string, string[]>>({});
  const [statsError, setStatsError] = useState<string | null>(null);
  const [chartDays, setChartDays] = useState<number>(() => readStoredChartDays());
  const [granularity, setGranularity] = useState<Granularity>(() => readStoredGranularity());
  const [presentation, setPresentation] = useState<Presentation>(() => readStoredPresentation());
  const [venuesTrendBaseline, setVenuesTrendBaseline] = useState(0);
  const [topCities, setTopCities] = useState<TopCity[]>([]);
  const [topVenues, setTopVenues] = useState<TopVenue[]>([]);
  const [topContributors, setTopContributors] = useState<TopContributor[]>([]);
  const [recentSignups, setRecentSignups] = useState<RecentSignup[]>([]);
  const [recentRatings, setRecentRatings] = useState<RecentRating[]>([]);
  const [last24h, setLast24h] = useState<Last24hStats | null>(null);
  const [health, setHealth] = useState<HealthState | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  /**
   * Central day → the venue ids whose FIRST non-retired rating landed on that day. Computed here
   * because the "venues rated for the first time" bar needs every log ever, not just the window's;
   * handing the ids to the drill-down makes the list and the bar the same set by construction.
   */
  const [firstRatedVenueIdsByDay, setFirstRatedVenueIdsByDay] = useState<Record<string, string[]>>({});
  const [drilldown, setDrilldown] = useState<DrilldownTarget | null>(null);
  /** A weekly bar was clicked and covers more than one active day — pick which day to list. */
  const [weekPick, setWeekPick] = useState<WeekDrilldownTarget | null>(null);
  const [detailVenue, setDetailVenue] = useState<VenueDetailSeed | null>(null);
  /** Flips once the leaderboard/recent-activity load has run — drives their skeleton. */
  const [extrasLoaded, setExtrasLoaded] = useState(false);

  const badges = useNavBadges(true);
  const { count: unreadFeedback } = useInboxUnreadCount();

  const selectChartDays = useCallback((n: number) => {
    setChartDays(n);
    try {
      window.localStorage.setItem(CHART_DAYS_STORAGE_KEY, String(n));
    } catch {
      /* ignore persistence failures */
    }
  }, []);

  const selectGranularity = useCallback((v: Granularity) => {
    setGranularity(v);
    try {
      window.localStorage.setItem(CHART_GRANULARITY_STORAGE_KEY, v);
    } catch {
      /* ignore persistence failures */
    }
  }, []);

  const selectPresentation = useCallback((v: Presentation) => {
    setPresentation(v);
    try {
      window.localStorage.setItem(CHART_PRESENTATION_STORAGE_KEY, v);
    } catch {
      /* ignore persistence failures */
    }
  }, []);

  const loadStats = useCallback(async () => {
    setStatsError(null);
    try {
      const d7 = isoDaysAgo(7);
      const d30 = isoDaysAgo(30);
      // +1 day of buffer so the UTC fetch window fully covers the oldest Central day.
      const dChart = isoDaysAgo(chartDays + 1);

      // Each metric loads independently so one missing table / blocked grant
      // (e.g. app_sessions for active users) degrades to 0 / "—" instead of
      // blanking the whole dashboard.
      const countOf = async (
        q: PromiseLike<{ count: number | null; error: { message: string } | null }>,
      ): Promise<number | null> => {
        try {
          const { count, error } = await q;
          return error ? null : count ?? 0;
        } catch {
          return null;
        }
      };
      const rowsOf = async <T,>(p: Promise<T[]>): Promise<T[]> => {
        try {
          return await p;
        } catch {
          return [];
        }
      };

      const [
        totalUsers,
        newUsers7d,
        totalRatings,
        ratings7d,
        citiesWithRaters,
        active7d,
        active30d,
        logRows,
        profilesRecent,
        logsRecent,
      ] = await Promise.all([
        countOf(supabase.from('profiles').select('id', { count: 'exact', head: true })),
        countOf(supabase.from('profiles').select('id', { count: 'exact', head: true }).gte('created_at', d7)),
        countOf(supabase.from('logs').select('id', { count: 'exact', head: true }).is('retired_at', null)),
        countOf(
          supabase.from('logs').select('id', { count: 'exact', head: true }).is('retired_at', null).gte('created_at', d7),
        ),
        countOf(supabase.from('cities').select('id', { count: 'exact', head: true }).gt('total_shado_ratings', 0)),
        countActiveUsersSince(d7),
        countActiveUsersSince(d30),
        rowsOf(
          fetchAllRows<{ venue_id: string | null; user_id: string | null; created_at: string }>((from, to) =>
            supabase.from('logs').select('venue_id, user_id, created_at').is('retired_at', null).range(from, to),
          ),
        ),
        rowsOf(
          fetchAllRows<{ created_at: string }>((from, to) =>
            supabase.from('profiles').select('created_at').gte('created_at', dChart).range(from, to),
          ),
        ),
        rowsOf(
          fetchAllRows<{ created_at: string; venue_id: string | null }>((from, to) =>
            supabase
              .from('logs')
              .select('created_at, venue_id')
              .is('retired_at', null)
              .gte('created_at', dChart)
              .range(from, to),
          ),
        ),
      ]);

      // Hard-fail only if the most basic query (total users) couldn't load — that
      // signals an auth/connection problem, not a single missing table.
      if (totalUsers == null) {
        setStats(null);
        setStatsError('Failed to load stats — check your admin session and connection, then refresh.');
        return;
      }

      const dayTemplate = buildLastNDaysTemplate(chartDays);

      // "Distinct venues" honestly: bucket each venue on the Central day of its
      // FIRST rating, so the bars sum to real growth (a venue never counts twice)
      // and the trend line is the cumulative distinct total.
      const firstDays = firstRatingDayByVenue(logRows);
      const windowStart = dayTemplate[0].iso;
      const firstTimeCounts = new Map<string, number>();
      // Same walk also records WHICH venues each bar counted, for the drill-down.
      const firstTimeIds: Record<string, string[]> = {};
      let venuesBeforeWindow = 0;
      firstDays.forEach((day, venueId) => {
        if (day < windowStart) {
          venuesBeforeWindow += 1;
          return;
        }
        firstTimeCounts.set(day, (firstTimeCounts.get(day) ?? 0) + 1);
        (firstTimeIds[day] ?? (firstTimeIds[day] = [])).push(venueId);
      });

      // Distinct raters, honestly: the BAR is the users who rated on that Central day (each user
      // once per day), while the headline and trend line are users who have EVER rated — bucketed
      // on each user's FIRST rating day, so a nightly regular never inflates the line.
      const firstDayByUser = new Map<string, string>();
      const activeIdsByDay = new Map<string, Set<string>>();
      for (const r of logRows) {
        const uid = r.user_id;
        if (!uid) continue;
        const day = dayKey(r.created_at);
        const prev = firstDayByUser.get(uid);
        if (!prev || day < prev) firstDayByUser.set(uid, day);
        if (day >= windowStart) {
          let set = activeIdsByDay.get(day);
          if (!set) activeIdsByDay.set(day, (set = new Set()));
          set.add(uid);
        }
      }
      const raterFirstCounts = new Map<string, number>();
      const firstRaterIds: Record<string, string[]> = {};
      let ratersBeforeWindow = 0;
      firstDayByUser.forEach((day, uid) => {
        if (day < windowStart) {
          ratersBeforeWindow += 1;
          return;
        }
        raterFirstCounts.set(day, (raterFirstCounts.get(day) ?? 0) + 1);
        (firstRaterIds[day] ?? (firstRaterIds[day] = [])).push(uid);
      });
      const activeArraysByDay: Record<string, string[]> = {};
      activeIdsByDay.forEach((set, day) => {
        activeArraysByDay[day] = Array.from(set);
      });

      setRatersActiveByDay(activeArraysByDay);
      setFirstRaterIdsByDay(firstRaterIds);
      setRatersFirstSeries(mergeCountsIntoSeries(dayTemplate, raterFirstCounts));
      setRatersTrendBaseline(ratersBeforeWindow);

      setUserSignupSeries(mergeCountsIntoSeries(dayTemplate, countByDay(profilesRecent)));
      setRatingsSeries(mergeCountsIntoSeries(dayTemplate, countByDay(logsRecent)));
      setVenuesRatedDaySeries(mergeCountsIntoSeries(dayTemplate, firstTimeCounts));
      setFirstRatedVenueIdsByDay(firstTimeIds);
      setVenuesTrendBaseline(venuesBeforeWindow);

      setStats({
        totalUsers,
        newUsers7d: newUsers7d ?? 0,
        venuesRatedDistinct: firstDays.size,
        distinctRaters: firstDayByUser.size,
        totalRatings: totalRatings ?? 0,
        ratings7d: ratings7d ?? 0,
        citiesWithRaters: citiesWithRaters ?? 0,
        active7d,
        active30d,
      });
      setUpdatedAt(new Date());
    } catch (e: unknown) {
      setStats(null);
      setUserSignupSeries([]);
      setRatingsSeries([]);
      setVenuesRatedDaySeries([]);
      setFirstRatedVenueIdsByDay({});
      setVenuesTrendBaseline(0);
      setRatersActiveByDay({});
      setRatersFirstSeries([]);
      setRatersTrendBaseline(0);
      setFirstRaterIdsByDay({});
      setStatsError(e instanceof Error ? e.message : 'Failed to load stats');
    }
  }, [chartDays]);

  useEffect(() => {
    void loadStats();
  }, [loadStats]);

  // "Last 24 hours" strip — loads independently (own fail-soft try/catch per metric) so a
  // missing table/grant degrades a single tile to "—" instead of blanking the section.
  const loadLast24h = useCallback(async () => {
    const h24 = isoHoursAgo(24);
    const countOf = async (
      q: PromiseLike<{ count: number | null; error: { message: string } | null }>,
    ): Promise<number | null> => {
      try {
        const { count, error } = await q;
        return error ? null : count ?? 0;
      } catch {
        return null;
      }
    };
    const [newUsers, ratings, photosSubmitted, photosApproved] = await Promise.all([
      countOf(supabase.from('profiles').select('id', { count: 'exact', head: true }).gte('created_at', h24)),
      countOf(
        supabase.from('logs').select('id', { count: 'exact', head: true }).is('retired_at', null).gte('created_at', h24),
      ),
      countOf(supabase.from('venue_photos').select('id', { count: 'exact', head: true }).gte('created_at', h24)),
      countOf(supabase.from('venue_photos').select('id', { count: 'exact', head: true }).gte('approved_at', h24)),
    ]);
    setLast24h({ newUsers, ratings, photosSubmitted, photosApproved });
  }, []);

  useEffect(() => {
    void loadLast24h();
  }, [loadLast24h]);

  // Leaderboards + recent activity load independently of the KPI/chart block so
  // a missing column / blocked grant degrades to an empty list, never blanking
  // the dashboard. These are time-range-independent (load once on mount).
  const loadExtras = useCallback(async () => {
    const safe = async <T,>(p: PromiseLike<{ data: T[] | null; error: unknown }>): Promise<T[]> => {
      try {
        const { data, error } = await p;
        return error ? [] : data ?? [];
      } catch {
        return [];
      }
    };

    const [cities, venues, signups, ratings, contributors] = await Promise.all([
      safe<TopCity>(
        supabase
          .from('cities')
          .select('id, name, total_shado_ratings')
          .order('total_shado_ratings', { ascending: false, nullsFirst: false })
          .limit(10),
      ),
      safe<TopVenue>(
        supabase
          .from('venues')
          .select('id, venue_name_user_input, venue_shado_rating_count')
          .order('venue_shado_rating_count', { ascending: false, nullsFirst: false })
          .limit(10),
      ),
      safe<RecentSignup>(
        supabase
          .from('profiles')
          .select('id, username, created_at')
          .order('created_at', { ascending: false })
          .limit(15),
      ),
      safe<RecentRating>(
        supabase
          .from('logs')
          .select('id, venue_id, venue_name, created_at')
          .is('retired_at', null)
          .order('created_at', { ascending: false })
          .limit(15),
      ),
      fetchTopPhotoContributors(10),
    ]);

    setTopCities(cities);
    setTopVenues(venues);
    setRecentSignups(signups);
    setRecentRatings(ratings);
    setTopContributors(contributors);
    setExtrasLoaded(true);
  }, []);

  useEffect(() => {
    void loadExtras();
  }, [loadExtras]);

  // System-health strip. Every read is fail-soft: an unreadable table (RLS/grant)
  // turns its segment into "no signal" / hides it — never an error banner.
  const loadHealth = useCallback(async () => {
    const h24 = isoHoursAgo(24);

    const daemon = await (async (): Promise<{ readable: boolean; beatAt: string | null }> => {
      try {
        const { data, error } = await supabase
          .from('daemon_heartbeat')
          .select('last_beat_at')
          .order('last_beat_at', { ascending: false })
          .limit(1);
        if (error) return { readable: false, beatAt: null };
        return { readable: true, beatAt: data?.[0]?.last_beat_at ?? null };
      } catch {
        return { readable: false, beatAt: null };
      }
    })();

    const sentry = await (async (): Promise<{ count: number | null; topTitle: string | null }> => {
      try {
        const { count, error } = await supabase
          .from('sentry_alert_log')
          .select('issue_id', { count: 'exact', head: true })
          .gte('last_alerted_at', h24);
        if (error) return { count: null, topTitle: null };
        let topTitle: string | null = null;
        if ((count ?? 0) > 0) {
          const { data } = await supabase
            .from('sentry_alert_log')
            .select('title')
            .gte('last_alerted_at', h24)
            .order('last_alerted_at', { ascending: false })
            .limit(1);
          topTitle = data?.[0]?.title ?? null;
        }
        return { count: count ?? 0, topTitle };
      } catch {
        return { count: null, topTitle: null };
      }
    })();

    const cron = await (async (): Promise<number | null> => {
      try {
        const { count, error } = await supabase
          .from('cron_alarm_state')
          .select('job', { count: 'exact', head: true })
          .gte('last_alerted_at', h24);
        return error ? null : count ?? 0;
      } catch {
        return null;
      }
    })();

    setHealth({
      daemonBeatAt: daemon.beatAt,
      daemonReadable: daemon.readable,
      sentry24h: sentry.count,
      sentryTopTitle: sentry.topTitle,
      cronAlarm24h: cron,
    });
  }, []);

  useEffect(() => {
    void loadHealth();
  }, [loadHealth]);

  const refreshAll = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([loadStats(), loadExtras(), loadHealth(), loadLast24h()]);
    } finally {
      setRefreshing(false);
    }
  }, [loadStats, loadExtras, loadHealth, loadLast24h]);

  /**
   * Per-day points, always computed. `cumulative` = baseline (everything before the window) + the
   * window's increments, so the line ends at today's real total. These stay per-day even in Weekly
   * view because the drill-down still resolves to a single Central day.
   */
  const usersDailyPoints = useMemo(
    () =>
      stats
        ? buildDailyPoints(userSignupSeries, Math.max(0, stats.totalUsers - sumOf(userSignupSeries)))
        : [],
    [stats, userSignupSeries],
  );
  const ratingsDailyPoints = useMemo(
    () =>
      stats
        ? buildDailyPoints(ratingsSeries, Math.max(0, stats.totalRatings - sumOf(ratingsSeries)))
        : [],
    [stats, ratingsSeries],
  );
  const venuesDailyPoints = useMemo(
    () => buildDailyPoints(venuesRatedDaySeries, venuesTrendBaseline),
    [venuesRatedDaySeries, venuesTrendBaseline],
  );

  // What actually gets plotted: the same points, optionally grouped into Monday-start weeks.
  const usersPoints = useMemo(
    () => (granularity === 'weekly' ? groupPointsByWeek(usersDailyPoints) : usersDailyPoints),
    [granularity, usersDailyPoints],
  );
  const ratingsPoints = useMemo(
    () => (granularity === 'weekly' ? groupPointsByWeek(ratingsDailyPoints) : ratingsDailyPoints),
    [granularity, ratingsDailyPoints],
  );
  const venuesPoints = useMemo(
    () => (granularity === 'weekly' ? groupPointsByWeek(venuesDailyPoints) : venuesDailyPoints),
    [granularity, venuesDailyPoints],
  );
  /**
   * Raters need their own builders because the bar is a SET SIZE, not a sum: daily bars count each
   * user once per day, and a weekly bar is the union of its days' user sets (a nightly regular
   * counts once, so grouping can't just add the days like the other charts do). The line is
   * "users who have ever rated", accumulated from each user's first rating day.
   */
  const ratersDailyPoints = useMemo(() => {
    let acc = ratersTrendBaseline;
    return ratersFirstSeries.map((p) => {
      acc += p.value;
      const active = ratersActiveByDay[p.iso]?.length ?? 0;
      return {
        key: p.iso,
        label: p.label,
        value: active,
        cumulative: acc,
        days: [{ iso: p.iso, label: p.label, value: active }],
        partial: false,
      };
    });
  }, [ratersFirstSeries, ratersTrendBaseline, ratersActiveByDay]);
  /** "Rated at least one venue": bars = people whose FIRST-ever rating was that day; the line is
   * the running total of people who have ever rated. First-time counts sum cleanly across weeks,
   * so the standard grouping applies. */
  const newRatersDailyPoints = useMemo(
    () => buildDailyPoints(ratersFirstSeries, ratersTrendBaseline),
    [ratersFirstSeries, ratersTrendBaseline],
  );
  const newRatersPoints = useMemo(
    () => (granularity === 'weekly' ? groupPointsByWeek(newRatersDailyPoints) : newRatersDailyPoints),
    [granularity, newRatersDailyPoints],
  );
  const ratersPoints = useMemo(() => {
    if (granularity !== 'weekly') return ratersDailyPoints;
    const weeks = groupPointsByWeek(ratersDailyPoints);
    for (const w of weeks) {
      const union = new Set<string>();
      for (const d of w.days) for (const id of ratersActiveByDay[d.iso] ?? []) union.add(id);
      w.value = union.size;
    }
    return weeks;
  }, [granularity, ratersDailyPoints, ratersActiveByDay]);

  const daemonTone: 'ok' | 'warn' | 'none' = !health?.daemonReadable
    ? 'none'
    : health.daemonBeatAt && Date.now() - new Date(health.daemonBeatAt).getTime() < DAEMON_STALE_MS
      ? 'ok'
      : 'warn';

  const secondaryStatCards: { label: string; value: number | null; sub?: string }[] = stats
    ? [
        { label: 'New users (7d)', value: stats.newUsers7d },
        { label: 'Ratings (7d)', value: stats.ratings7d },
        { label: 'Cities with raters', value: stats.citiesWithRaters },
        { label: 'Users active (7d)', value: stats.active7d, sub: 'Opened the app in the last 7 days' },
        { label: 'Users active (30d)', value: stats.active30d, sub: 'Opened the app in the last 30 days' },
      ]
    : [];

  const dailyPointsFor = useCallback(
    (kind: DaySeriesKind): ChartPoint[] =>
      kind === 'signups'
        ? usersDailyPoints
        : kind === 'ratings'
          ? ratingsDailyPoints
          : kind === 'raters'
            ? ratersDailyPoints
            : kind === 'new-raters'
              ? newRatersDailyPoints
              : venuesDailyPoints,
    [usersDailyPoints, ratingsDailyPoints, ratersDailyPoints, newRatersDailyPoints, venuesDailyPoints],
  );

  /**
   * One Central calendar day was resolved (directly in Daily view, or through the week picker).
   * `dayIso` is the same key the chart bucketed on, and the drill-down re-derives its SQL window
   * from that string via the shared `tzDayRangeUtc` — chart and query cannot disagree.
   *
   * Bar view plots the increment, Trend and Combined plot the cumulative, so the running total is
   * only meaningful (and only shown) in the latter two.
   */
  const openDayDrilldown = useCallback(
    (kind: DaySeriesKind, dayIso: string, dayLabel: string) => {
      const day = dailyPointsFor(kind).find((p) => p.key === dayIso);
      setDrilldown({
        kind,
        dayIso,
        dayLabel,
        dayValue: day?.value ?? 0,
        runningTotal: presentation === 'bar' ? null : day?.cumulative ?? null,
        venueIds: kind === 'venues' ? firstRatedVenueIdsByDay[dayIso] ?? [] : undefined,
        userIds: kind === 'new-raters' ? firstRaterIdsByDay[dayIso] ?? [] : undefined,
      });
    },
    [presentation, dailyPointsFor, firstRatedVenueIdsByDay, firstRaterIdsByDay],
  );

  /**
   * A plotted bucket was clicked. A day goes straight to the rows. A week has no single SQL window,
   * so it opens the picker — unless exactly one of its days actually had activity, in which case
   * the picker would be a pointless click and we go straight there.
   */
  const openBucketDrilldown = useCallback(
    (kind: DaySeriesKind, point: ChartPoint) => {
      const active = point.days.filter((d) => d.value > 0);
      if (point.days.length === 1) {
        openDayDrilldown(kind, point.days[0].iso, point.days[0].label);
        return;
      }
      if (active.length === 1) {
        openDayDrilldown(kind, active[0].iso, active[0].label);
        return;
      }
      setWeekPick({
        kind,
        weekLabel: point.label,
        total: point.value,
        runningTotal: presentation === 'bar' ? null : point.cumulative,
        partial: point.partial,
        days: point.days.map((d) => ({ iso: d.iso, label: d.label, value: d.value })),
      });
    },
    [presentation, openDayDrilldown],
  );

  const pickWeekDay = useCallback(
    (day: WeekPickerDay) => {
      const kind = weekPick?.kind;
      setWeekPick(null);
      if (kind) openDayDrilldown(kind, day.iso, day.label);
    },
    [weekPick, openDayDrilldown],
  );

  /**
   * Venue rows seed `CityVenueDetailModal` with LIVE counts off `logs`, not the denormalized
   * `venue_shado_*` columns — seeding from the columns would make that modal's drift warning
   * compare a value to itself and never fire.
   */
  const openVenueDetail = useCallback(async (venueId: string, name: string) => {
    setDetailVenue(await fetchVenueSeed(venueId, name));
  }, []);

  const rankedList = (
    rows: { key: string; name: string; count: number }[],
    emptyTitle: string,
    emptyHint: string,
    onRowClick?: (row: { key: string; name: string; count: number }) => void,
  ) =>
    rows.length === 0 ? (
      <EmptyState compact title={emptyTitle} hint={emptyHint} />
    ) : (
      <div className="space-y-0.5">
        {rows.map((r, i) => {
          const inner = (
            <>
              <span className="w-5 shrink-0 text-right tabular-nums text-mutedDim">{i + 1}</span>
              <span className="min-w-0 flex-1 truncate text-textPrimary">{r.name}</span>
              <span className="shrink-0 tabular-nums text-muted">{r.count.toLocaleString()}</span>
            </>
          );
          const cls =
            'flex items-center gap-3 rounded-md px-2 py-1.5 text-[13px] transition-colors hover:bg-white/[0.03]';
          return onRowClick ? (
            <button
              key={r.key}
              type="button"
              onClick={() => onRowClick(r)}
              className={`${cls} w-full text-left hover:text-accent`}
            >
              {inner}
            </button>
          ) : (
            <div key={r.key} className={cls}>
              {inner}
            </div>
          );
        })}
      </div>
    );

  return (
    <div className="admin-page max-w-6xl mx-auto">
      <PageHeader
        title="Overview"
        subtitle="Live production counts — health, queues, growth."
        actions={
          <>
            {updatedAt ? (
              <span className="text-[11px] tabular-nums text-mutedDim">
                Updated {timeAgo(updatedAt.toISOString())}
              </span>
            ) : null}
            <button
              type="button"
              onClick={() => void refreshAll()}
              disabled={refreshing}
              className="admin-btn admin-btn-secondary admin-btn-sm"
            >
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </>
        }
      />

      {statsError ? <ErrorBanner className="mb-6">{statsError}</ErrorBanner> : null}

      {/* 1 · Growth — totals + drillable day charts. Always expanded: the charts are Recharts
          `ResponsiveContainer`s, which measure 0×0 in a hidden parent, so they must stay mounted
          and laid out for the day-click drill-down to work. */}
      {!stats && !statsError ? (
        <SkeletonCards cards={3} className="lg:grid-cols-3" />
      ) : stats ? (
        <div className="mb-6 space-y-4">
          {/* Two independent axes: how wide a bucket is, then how it is drawn. */}
          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              <div className="flex items-center gap-1">
                <span className="admin-eyebrow mr-2">Bucket</span>
                {GRANULARITY_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => selectGranularity(o.value)}
                    data-active={granularity === o.value}
                    className="admin-pill"
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1">
                <span className="admin-eyebrow mr-2">Chart</span>
                {PRESENTATION_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => selectPresentation(o.value)}
                    data-active={presentation === o.value}
                    className="admin-pill"
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <span className="admin-eyebrow mr-2">Range</span>
              {RANGE_OPTIONS.map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => selectChartDays(o.v)}
                  data-active={chartDays === o.v}
                  className="admin-pill"
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
          {/* The quadrant: four core series, 2×2 on desktop (Evan 2026-09-04). */}
          <div className="admin-hero-wash grid grid-cols-1 gap-4 rounded-lg lg:grid-cols-2">
            <div className="min-w-0 lg:col-span-2">
            <StatCard label="Total users" value={stats.totalUsers}>
              <StatSpark
                presentation={presentation}
                granularity={granularity}
                data={usersPoints}
                onPointClick={(p) => openBucketDrilldown('signups', p)}
                caption={chartCaption(presentation, granularity, chartDays, 'New signups', 'Total users')}
              />
              <ChartMoreButton title="Total users" bucketNoun={granularity === 'weekly' ? 'weeks' : 'days'} valueNoun="new signups" points={usersPoints} />
            </StatCard>
            </div>
            <StatCard label="Total ratings" value={stats.totalRatings}>
              <StatSpark
                presentation={presentation}
                granularity={granularity}
                data={ratingsPoints}
                onPointClick={(p) => openBucketDrilldown('ratings', p)}
                caption={chartCaption(
                  presentation,
                  granularity,
                  chartDays,
                  'Rating events (logs)',
                  'Total ratings',
                )}
              />
              <ChartMoreButton title="Total ratings" bucketNoun={granularity === 'weekly' ? 'weeks' : 'days'} valueNoun="ratings" points={ratingsPoints} />
            </StatCard>
            <StatCard label="Venues with ≥1 rating" value={stats.venuesRatedDistinct}>
              <StatSpark
                presentation={presentation}
                granularity={granularity}
                data={venuesPoints}
                onPointClick={(p) => openBucketDrilldown('venues', p)}
                caption={chartCaption(
                  presentation,
                  granularity,
                  chartDays,
                  'Venues rated for the first time',
                  'Distinct venues ever rated',
                )}
              />
              <ChartMoreButton title="Venues with ≥1 rating" bucketNoun={granularity === 'weekly' ? 'weeks' : 'days'} valueNoun="first-time venues" points={venuesPoints} />
            </StatCard>
            <StatCard label="Distinct raters" value={stats.distinctRaters}>
              <StatSpark
                presentation={presentation}
                granularity={granularity}
                data={ratersPoints}
                onPointClick={(p) => openBucketDrilldown('raters', p)}
                caption={chartCaption(
                  presentation,
                  granularity,
                  chartDays,
                  'Distinct raters',
                  'Users who have ever rated',
                )}
              />
              <ChartMoreButton title="Distinct raters" bucketNoun={granularity === 'weekly' ? 'weeks' : 'days'} valueNoun="distinct raters" points={ratersPoints} />
            </StatCard>
            <StatCard label="Rated at least one venue" value={stats.distinctRaters}>
              <StatSpark
                presentation={presentation}
                granularity={granularity}
                data={newRatersPoints}
                onPointClick={(p) => openBucketDrilldown('new-raters', p)}
                caption={chartCaption(
                  presentation,
                  granularity,
                  chartDays,
                  'First-ever raters',
                  'People who have rated ≥1 venue',
                )}
              />
              <ChartMoreButton title="Rated at least one venue" bucketNoun={granularity === 'weekly' ? 'weeks' : 'days'} valueNoun="first-time raters" points={newRatersPoints} />
            </StatCard>
          </div>
          <p className="text-[10px] text-mutedDim">
            Days cut at midnight Central ({DAY_TZ}); weeks are Monday–Sunday, grouped from those same
            day buckets — so a Saturday night that spills past midnight stays in the week it belongs to,
            and the first/last week of a range can be partial. Combined draws the cumulative line over
            per-bucket volume bars on one shared x-axis. “Venues with ≥1 rating” counts DISTINCT venues
            that have ever been rated — the bars are venues crossing their FIRST rating, and the line is
            that total accumulating, not a per-bucket count. “Distinct raters” bars count each user
            once per day (a weekly bar is the union of its days, so a nightly regular still counts
            once); its line is users who have ever rated, accumulating from each user’s first rating.
          </p>
        </div>
      ) : null}

      {/* Last 24 hours — DB-truth, same fail-soft-to-"—" convention as the rest of the page. */}
      <div className="mb-6">
        <div className="admin-eyebrow mb-2">Last 24 hours</div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <StatCard label="New users" value={last24h?.newUsers ?? null} />
          <StatCard label="New ratings" value={last24h?.ratings ?? null} />
          <StatCard label="Photos submitted" value={last24h?.photosSubmitted ?? null} />
          <StatCard label="Photos approved" value={last24h?.photosApproved ?? null} />
        </div>
      </div>

      {/* 3 · This week */}
      <div className="mb-6">
        <div className="admin-eyebrow mb-2">This week</div>
        {stats ? (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-5">
            {secondaryStatCards.map((c) => (
              <StatCard key={c.label} label={c.label} value={c.value} sub={c.sub} />
            ))}
          </div>
        ) : (
          <SkeletonCards cards={5} />
        )}
      </div>

      {/* System health strip — quiet when green, loud only when something needs eyes. */}
      {health ? (
        <div className="admin-card mb-4 flex flex-wrap items-center gap-x-8 gap-y-2 !py-3">
          <HealthSegment
            tone={daemonTone}
            label="Daemon"
            detail={
              !health.daemonReadable
                ? 'no signal'
                : health.daemonBeatAt
                  ? `beat ${timeAgo(health.daemonBeatAt)}`
                  : 'no heartbeat recorded'
            }
          />
          <HealthSegment
            tone={health.sentry24h == null ? 'none' : health.sentry24h > 0 ? 'warn' : 'ok'}
            label="Sentry"
            detail={
              health.sentry24h == null
                ? 'no signal'
                : health.sentry24h === 0
                  ? 'quiet · no alerts in 24h'
                  : `${health.sentry24h} alert${health.sentry24h === 1 ? '' : 's'} in 24h${
                      health.sentryTopTitle ? ` · ${health.sentryTopTitle}` : ''
                    }`
            }
          />
          {health.cronAlarm24h != null ? (
            <HealthSegment
              tone={health.cronAlarm24h > 0 ? 'warn' : 'ok'}
              label="Cron"
              detail={health.cronAlarm24h === 0 ? 'quiet · no alarms in 24h' : `${health.cronAlarm24h} alarm${health.cronAlarm24h === 1 ? '' : 's'} in 24h`}
            />
          ) : null}
        </div>
      ) : null}

      {/* Work queues — what needs an operator right now. */}
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <QueueCard to="/venues" label="Pending photos" count={badges.pendingVenuePhotos} verb="Review" />
        <QueueCard to="/reverse-search" label="Foursquare queue" count={badges.pendingQueue} verb="Resolve" />
        <QueueCard to="/reports" label="Open reports" count={badges.openReports} verb="Triage" />
        <QueueCard to="/feedback" label="Unread feedback" count={unreadFeedback} verb="Read" />
      </div>

      {/* Rest · leaderboards + recent activity */}
      {!extrasLoaded ? (
        <SkeletonCards cards={3} className="lg:grid-cols-3" />
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="admin-card min-w-0">
              <div className="admin-eyebrow mb-3">Top cities · by ratings</div>
              {rankedList(
                topCities.map((c) => ({ key: c.id, name: c.name ?? '—', count: c.total_shado_ratings ?? 0 })),
                'No rated cities yet',
                'Cities appear here once venues in them get Shado ratings.',
                (row) =>
                  setDrilldown({
                    kind: 'city',
                    cityId: row.key,
                    cityName: row.name,
                    ratingsColumn: row.count,
                  }),
              )}
            </div>
            <div className="admin-card min-w-0">
              <div className="admin-eyebrow mb-3">Top venues · by ratings</div>
              {rankedList(
                topVenues.map((v) => ({
                  key: v.id,
                  name: v.venue_name_user_input ?? '—',
                  count: v.venue_shado_rating_count ?? 0,
                })),
                'No rated venues yet',
                'Venues appear here once users log ratings.',
                (row) => void openVenueDetail(row.key, row.name),
              )}
            </div>
            <div className="admin-card min-w-0">
              <div className="admin-eyebrow mb-3">Top photo contributors · approved</div>
              {rankedList(
                topContributors.map((c) => ({
                  key: c.user_id,
                  name: c.username ? `@${c.username}` : `${c.user_id.slice(0, 8)}…`,
                  count: c.count,
                })),
                'No approved photos yet',
                'Contributors appear here once their submitted photos are approved in Venues.',
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="admin-card min-w-0">
              <div className="admin-eyebrow mb-3">Recent signups</div>
              {recentSignups.length === 0 ? (
                <EmptyState compact title="No signups yet" hint="New accounts show up here as they join." />
              ) : (
                <div className="space-y-0.5">
                  {recentSignups.map((u) => (
                    <div
                      key={u.id}
                      className="flex items-center gap-3 rounded-md px-2 py-1.5 text-[13px] transition-colors hover:bg-white/[0.03]"
                    >
                      <span className="min-w-0 flex-1 truncate text-textPrimary">{u.username ?? '—'}</span>
                      <span className="shrink-0 tabular-nums text-mutedDim">{timeAgo(u.created_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="admin-card min-w-0">
              <div className="admin-eyebrow mb-3">Recent ratings</div>
              {recentRatings.length === 0 ? (
                <EmptyState compact title="No ratings yet" hint="New rating logs show up here as users log venues." />
              ) : (
                <div className="space-y-0.5">
                  {recentRatings.map((r) => (
                    <div
                      key={r.id}
                      className="flex items-center gap-3 rounded-md px-2 py-1.5 text-[13px] transition-colors hover:bg-white/[0.03]"
                    >
                      <span className="min-w-0 flex-1 truncate text-textPrimary">
                        {r.venue_name ?? r.venue_id ?? '—'}
                      </span>
                      <span className="shrink-0 tabular-nums text-mutedDim">{timeAgo(r.created_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Drill-downs — "who / what is behind this number?" */}
      <WeekDrilldownPicker target={weekPick} onPickDay={pickWeekDay} onClose={() => setWeekPick(null)} />
      <OverviewDrilldownModal target={drilldown} onClose={() => setDrilldown(null)} />
      <CityVenueDetailModal venue={detailVenue} cityName={null} onClose={() => setDetailVenue(null)} />
    </div>
  );
}
